import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Upload, Download, Trash2, FileText, Image as ImgIcon, FileSpreadsheet, File as FileIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

const iconByMime = (mime, name) => {
  const n = (name || "").toLowerCase();
  if ((mime || "").startsWith("image/") || n.endsWith(".jpg") || n.endsWith(".jpeg") || n.endsWith(".png")) return ImgIcon;
  if ((mime || "") === "application/pdf" || n.endsWith(".pdf")) return FileText;
  if (n.endsWith(".xls") || n.endsWith(".xlsx")) return FileSpreadsheet;
  return FileIcon;
};

export default function LeadAnexos({ leadId, leadUnidadeId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/anexos`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar anexos");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/leads/${leadId}/anexos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} anexo(s) enviado(s)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const canDelete = (anexo) => {
    if (!user) return false;
    if (user.id === anexo.autor_id) return true;
    if ((user.role === "diretor" || user.role === "supervisao") && user.escopo === "geral") return true;
    if ((user.role === "diretor" || user.role === "supervisao") && user.unidade_id && user.unidade_id === leadUnidadeId) return true;
    return false;
  };

  const remover = async (anexo) => {
    if (!window.confirm(`Excluir o anexo "${anexo.filename}"?`)) return;
    try {
      await api.delete(`/leads/anexos/${anexo.id}`);
      toast.success("Anexo removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao remover");
    }
  };

  const baixar = async (anexo) => {
    try {
      const resp = await api.get(`/leads/anexos/${anexo.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = anexo.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Falha no download");
    }
  };

  const visualizar = async (anexo) => {
    const isImage = (anexo.mime_type || "").startsWith("image/");
    const isPdf = (anexo.mime_type || "") === "application/pdf";
    if (!isImage && !isPdf) {
      baixar(anexo);
      return;
    }
    try {
      const resp = await api.get(`/leads/anexos/${anexo.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: anexo.mime_type }));
      setPreviewUrl(url);
      setPreviewType(isImage ? "image" : "pdf");
    } catch (err) {
      toast.error("Falha ao abrir preview");
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewType(null);
  };

  return (
    <div className="space-y-4" data-testid="lead-anexos">
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={onUpload} className="hidden" data-testid="anexo-input" />
        <Button onClick={onPick} disabled={uploading} className="text-white" style={{ background: "#1B3A6B" }} data-testid="anexar-btn">
          {uploading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Enviando…</> : <><Upload size={14} className="mr-2" /> Anexar arquivo</>}
        </Button>
        <span className="text-xs text-muted-foreground">PDF, DOC/DOCX, XLS/XLSX, JPG, PNG — até 10MB por arquivo</span>
      </div>

      {items.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">Nenhum anexo neste lead.</Card>
      ) : (
        <div className="space-y-2">
          {items.map((a) => {
            const Icon = iconByMime(a.mime_type, a.filename);
            return (
              <Card key={a.id} className="p-3 flex items-center gap-3" data-testid={`anexo-${a.id}`}>
                <Icon size={28} style={{ color: "#1B3A6B" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {fmtSize(a.size_bytes)} · {a.autor_nome} · {fmtData(a.criado_em)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => visualizar(a)} title="Visualizar / download">
                    <Download size={14} />
                  </Button>
                  {canDelete(a) && (
                    <Button size="sm" variant="ghost" onClick={() => remover(a)} title="Excluir" data-testid={`anexo-del-${a.id}`}>
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closePreview} data-testid="anexo-preview">
          <Button size="sm" variant="secondary" className="absolute top-4 right-4 z-10" onClick={closePreview}>
            <X size={14} className="mr-1" /> Fechar
          </Button>
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-auto" onClick={(e) => e.stopPropagation()}>
            {previewType === "image" ? (
              <img src={previewUrl} alt="preview" className="w-full" />
            ) : (
              <iframe src={previewUrl} title="preview" className="w-full" style={{ height: "80vh" }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
